import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Archive,
  ArchiveRestore,
  Building2,
  Globe,
  Lock,
  Pencil,
  Plus,
  Share2,
  Users,
} from 'lucide-react'
import clsx from 'clsx'
import aiEngineApi, { VISIBILIDADES } from '../../services/aiEngineApi'
import { formatDateTime } from '../../utils/format'

const ICONE_VISIBILIDADE = { privada: Lock, tenant: Building2, global: Globe }

/** O motor guarda os tempos em epoch de segundos; o util central espera Date/ISO. */
function instante(epoch) {
  if (!epoch) return '—'
  return formatDateTime(new Date(epoch * 1000))
}

function Selo({ children, className }) {
  return (
    <span
      className={clsx(
        'flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px]',
        className || 'bg-theme-bg text-theme-text-muted',
      )}
    >
      {children}
    </span>
  )
}

/** Quem abriu a conversa + quem mais escreveu nela (numa conversa compartilhada muda de mão). */
function Quem({ sessao }) {
  const dono = sessao.usuario_nome
  const outros = (sessao.participantes || []).filter((p) => p && p !== dono)
  if (!dono && outros.length === 0) return null
  return (
    <span className="flex min-w-0 items-center gap-1 text-[11px] text-theme-text-muted">
      <Users className="h-3 w-3 shrink-0" />
      <span className="truncate">
        {dono || 'sem dono'}
        {outros.length > 0 && ` + ${outros.join(', ')}`}
      </span>
    </span>
  )
}

/** Menu de compartilhamento. Só aparece para o dono — o motor recusa os demais com 403. */
function MenuCompartilhar({ sessao, onEscolher, onFechar }) {
  const ref = useRef(null)

  useEffect(() => {
    const fora = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onFechar()
    }
    document.addEventListener('mousedown', fora)
    return () => document.removeEventListener('mousedown', fora)
  }, [onFechar])

  const atual = sessao.visibilidade || 'privada'

  return (
    <div
      ref={ref}
      className="absolute right-2 top-8 z-20 w-64 overflow-hidden rounded-theme-md border border-theme-border bg-theme-surface shadow-lg"
    >
      {Object.values(VISIBILIDADES).map((v) => {
        const Icone = ICONE_VISIBILIDADE[v.valor]
        return (
          <button
            key={v.valor}
            type="button"
            onClick={() => onEscolher(v.valor)}
            className={clsx(
              'flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-theme-bg',
              atual === v.valor && 'bg-theme-bg',
            )}
          >
            <Icone className="mt-0.5 h-3.5 w-3.5 shrink-0 text-theme-text-muted" />
            <span className="min-w-0">
              <span className="block text-xs font-medium text-theme-text">
                {v.rotulo}
                {atual === v.valor && ' • atual'}
              </span>
              <span className="block text-[11px] leading-snug text-theme-text-muted">
                {v.descricao}
              </span>
            </span>
          </button>
        )
      })}
    </div>
  )
}

export default function ListaSessoes({ sessaoAtivaId, onSelecionar, onNova, onArquivada, criando }) {
  const queryClient = useQueryClient()
  const [arquivadas, setArquivadas] = useState(false)
  const [renomeando, setRenomeando] = useState(null)
  const [titulo, setTitulo] = useState('')
  const [menuAberto, setMenuAberto] = useState(null)

  const { data, isLoading } = useQuery({
    queryKey: ['ai-sessions', arquivadas],
    queryFn: () => aiEngineApi.listSessions(arquivadas),
    // Um turno rodando muda o selo e o "último uso"; recarregar de vez em quando mantém a
    // lista honesta sem precisar de mais um canal de tempo real.
    refetchInterval: 15000,
  })
  const sessoes = data?.sessions || []

  const invalidar = () => queryClient.invalidateQueries({ queryKey: ['ai-sessions'] })

  const renomear = useMutation({
    mutationFn: ({ id, title }) => aiEngineApi.renameSession(id, title),
    onSuccess: invalidar,
  })
  const compartilhar = useMutation({
    mutationFn: ({ id, visibilidade }) => aiEngineApi.setVisibilidade(id, visibilidade),
    onSuccess: invalidar,
  })
  const arquivar = useMutation({
    mutationFn: (id) => aiEngineApi.archiveSession(id),
    onSuccess: invalidar,
  })
  const restaurar = useMutation({
    mutationFn: (id) => aiEngineApi.unarchiveSession(id),
    onSuccess: invalidar,
  })

  async function confirmarRenomear() {
    if (!renomeando || !titulo.trim()) return
    await renomear.mutateAsync({ id: renomeando.id, title: titulo.trim() })
    setRenomeando(null)
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-theme-border p-2">
        <button
          type="button"
          onClick={onNova}
          disabled={criando}
          className="flex w-full items-center justify-center gap-1.5 rounded-theme-md bg-theme-primary px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          <Plus className="h-4 w-4" /> Nova conversa
        </button>
      </div>

      <div className="border-b border-theme-border px-3 py-1.5">
        <label className="flex cursor-pointer items-center gap-2 text-xs text-theme-text-muted">
          <input
            type="checkbox"
            checked={arquivadas}
            onChange={(e) => setArquivadas(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          Mostrar arquivadas
        </label>
      </div>

      <ul className="flex-1 divide-y divide-theme-border overflow-y-auto">
        {isLoading && <li className="p-4 text-sm text-theme-text-muted">Carregando…</li>}
        {!isLoading && sessoes.length === 0 && (
          <li className="p-4 text-sm text-theme-text-muted">
            {arquivadas ? 'Nenhuma conversa arquivada.' : 'Nenhuma conversa aberta.'}
          </li>
        )}

        {sessoes.map((s) => {
          const visibilidade = s.visibilidade || 'privada'
          const IconeVis = ICONE_VISIBILIDADE[visibilidade] || Lock
          return (
            <li key={s.id} className="group relative">
              <div
                role="button"
                tabIndex={0}
                onClick={() => onSelecionar(s.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') onSelecionar(s.id)
                }}
                className={clsx(
                  'w-full cursor-pointer px-3 py-2.5 text-left hover:bg-theme-bg',
                  sessaoAtivaId === s.id && 'bg-theme-bg',
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="truncate text-sm font-medium text-theme-text">
                    {s.title || 'Conversa sem título'}
                  </span>
                  <span className="shrink-0 text-[11px] text-theme-text-muted">
                    {instante(s.last_used_at)}
                  </span>
                </div>

                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <Selo>
                    <IconeVis className="h-3 w-3" />
                    {VISIBILIDADES[visibilidade]?.rotulo || visibilidade}
                  </Selo>
                  {s.tenant_name && <Selo>{s.tenant_name}</Selo>}
                  {s.running && (
                    // A paleta fixa só tem 500/600 (tailwind.config.js) — nada de -50/-800,
                    // que o Tailwind não gera e sairiam sem efeito nenhum.
                    <Selo className="bg-theme-bg font-medium text-warning-600">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-warning-500" />
                      rodando
                    </Selo>
                  )}
                  {s.archived_at && <Selo>arquivada</Selo>}
                  <span className="text-[10px] text-theme-text-muted">
                    {s.turn_count} {s.turn_count === 1 ? 'mensagem' : 'mensagens'}
                  </span>
                </div>

                <div className="mt-1">
                  <Quem sessao={s} />
                </div>
              </div>

              {/* Renomear, compartilhar e arquivar são do dono. */}
              {s.souDono && (
                <div className="absolute right-2 top-2 hidden gap-1 group-hover:flex">
                  <button
                    type="button"
                    title="Renomear"
                    onClick={() => {
                      setRenomeando(s)
                      setTitulo(s.title || '')
                    }}
                    className="rounded bg-theme-surface/90 p-1 text-theme-text-muted shadow-sm hover:text-theme-text"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    title="Compartilhar"
                    onClick={() => setMenuAberto(menuAberto === s.id ? null : s.id)}
                    className="rounded bg-theme-surface/90 p-1 text-theme-text-muted shadow-sm hover:text-theme-text"
                  >
                    <Share2 className="h-3.5 w-3.5" />
                  </button>
                  {s.archived_at ? (
                    <button
                      type="button"
                      title="Restaurar"
                      onClick={() => restaurar.mutate(s.id)}
                      className="rounded bg-theme-surface/90 p-1 text-theme-text-muted shadow-sm hover:text-theme-text"
                    >
                      <ArchiveRestore className="h-3.5 w-3.5" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      title="Arquivar — o histórico fica guardado"
                      onClick={() =>
                        arquivar.mutate(s.id, { onSuccess: () => onArquivada?.(s.id) })
                      }
                      className="rounded bg-theme-surface/90 p-1 text-theme-text-muted shadow-sm hover:text-theme-text"
                    >
                      <Archive className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              )}

              {menuAberto === s.id && (
                <MenuCompartilhar
                  sessao={s}
                  onFechar={() => setMenuAberto(null)}
                  onEscolher={(visibilidade) => {
                    setMenuAberto(null)
                    compartilhar.mutate({ id: s.id, visibilidade })
                  }}
                />
              )}
            </li>
          )
        })}
      </ul>

      {renomeando && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setRenomeando(null)} />
          <div className="relative w-full max-w-sm rounded-theme-md border border-theme-border bg-theme-surface p-4 shadow-xl">
            <h2 className="mb-3 text-sm font-semibold text-theme-text">Renomear conversa</h2>
            <input
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void confirmarRenomear()
                if (e.key === 'Escape') setRenomeando(null)
              }}
              autoFocus
              maxLength={120}
              className="w-full rounded-theme-md border border-theme-border bg-theme-bg px-3 py-2 text-sm text-theme-text focus:border-theme-primary focus:outline-none"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setRenomeando(null)}
                className="rounded-theme-md border border-theme-border px-3 py-1.5 text-sm text-theme-text-muted hover:text-theme-text"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void confirmarRenomear()}
                disabled={!titulo.trim()}
                className="rounded-theme-md bg-theme-primary px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
